import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Button from '@mui/material/Button';
import { clsx } from 'clsx';
// Components
import Text, { TextComponentProps } from '../text';
import Space, { Type } from '../layout/space';
import AvatarText from '../avatars';
import SvgIcon from '../icons/SvgIcon';
// Resources
import { stringsRes } from '../../resources/strings';
import { paths } from '../../navigation/routes/paths';
import { IconAxel } from '../../resources/images';
import useGlobalStyles from '../hook/useGlobalStyles';

interface ReservationLabelInfosProps {
    id?: string;
    label?: string;
    description?: string;
    descriptionColor?: TextComponentProps['customColor'];
    alwaysVisible?: boolean;
    iconElement?: JSX.Element;
    avatarValue?: string;
    onEdit?: () => void;
    endSpaceType?: Type;
    endSpaceValue?: number;
    containerClassName?: string;
    descriptionClassName?: string;
    ellipsisClassName?: boolean;
    noEllipsis?: boolean; // Maybe can be same as ellipsisClassName
    isMeetingPoint?: boolean;
}

const ReservationLabelInfos: FC<ReservationLabelInfosProps> = (props): ReactElement => {
    const {
        id,
        label,
        iconElement,
        description,
        descriptionColor = 'grey',
        alwaysVisible,
        avatarValue,
        onEdit,
        endSpaceType = Type.vertical,
        endSpaceValue = 1,
        containerClassName,
        descriptionClassName,
        ellipsisClassName = true,
        noEllipsis = false,
        isMeetingPoint = false,
    } = props;

    const classes = useStyles();
    const globalClasses = useGlobalStyles();

    const isHistory: boolean = window.location.pathname.startsWith(paths.tripHistory);

    if (description || alwaysVisible) {
        return (
            <>
                <div
                    id={id}
                    className={clsx(
                        classes.reservationLabelInfosContainer,
                        containerClassName,
                        'LCreservationLabelInfosContainer',
                    )}
                >
                    <div className={classes.labelInfoLeftContainer}>
                        {iconElement && <div className={classes.reservationLabelInfosIcon}>{iconElement}</div>}
                        {label && <Text.ShortRegularText14 text={label} className={classes.label} />}
                    </div>
                    {!isMeetingPoint && (
                        <Text.ShortRegularText14
                            text={description}
                            title={description}
                            customColor={descriptionColor}
                            className={clsx(!noEllipsis && classes.ellipsis, descriptionClassName)}
                            containerClassName={ellipsisClassName ? classes.sizedEllipsis : ''}
                        />
                    )}
                    {(avatarValue || (!isHistory && onEdit)) && <div className={classes.spacer} />}
                    {avatarValue && <AvatarText title={avatarValue} />}
                    {!isHistory && !isMeetingPoint && onEdit && (
                        <Button
                            id="edit"
                            className={clsx(classes.reservationLabelInfosButton, globalClasses.underline)}
                            onClick={onEdit}
                        >
                            {stringsRes.booking.edit}
                        </Button>
                    )}
                    {isMeetingPoint && (
                        <Button
                            id="edit"
                            className={clsx(
                                classes.reservationLabelInfosButton,
                                classes.grey,
                                onEdit && globalClasses.underline,
                            )}
                            onClick={onEdit || (() => {})}
                        >
                            {description}
                            {onEdit && (
                                <>
                                    <Space value={0.5} type={Type.horizontal} />
                                    <SvgIcon icon={IconAxel.Pencil} size="small" />
                                </>
                            )}
                        </Button>
                    )}
                </div>
                {endSpaceValue && <Space value={endSpaceValue} type={endSpaceType} />}
            </>
        );
    }
    return null;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        reservationLabelInfosContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        labelInfoLeftContainer: {
            display: 'flex',
            alignItems: 'center',
        },
        sizedEllipsis: {
            display: 'flex',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        reservationLabelInfosIcon: {
            marginRight: 12,
            color: theme.palette.colors.icon.lightGrey,
            display: 'flex',
            alignItems: 'center',
        },
        label: {
            marginRight: 5,
        },
        grey: {
            color: theme.palette.colors.text.label,
        },
        reservationLabelInfosButton: {
            // color: theme.palette.secondary.light,
            fontWeight: 400,
            justifyContent: 'flex-end',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        spacer: {
            width: theme.spacing(1),
        },
        ellipsis: {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
    }),
);

export default ReservationLabelInfos;
